// src/components/AgentCard.tsx
//
// The reusable representation of an FDW entity's agent card — reflects
// agentCards/{uid} exactly as published by publish_presence, fetched
// live from the public cardUrl (no auth needed — matches how a real
// buyer-side agent would read it too). Shows ALL live outcomes and
// skills in one card, not split by vertical — matches the real data
// shape (agentCards/{uid} is one document; fdw:outcomes and skills are
// both arrays on it).
//
// Visual structure informed by assistant-ui's real Agent Card
// component (pasted reference this session): compact icon+name header,
// monospace metadata row for the endpoint, restrained skill tags.
// Diverges deliberately in two ways: (1) skills and outcomes are kept
// as TWO separate sections, not one — FDW's real card data has both,
// and they mean different things (skills = what kind of interaction is
// possible; outcomes = specific priced work). Forcing outcomes into a
// generic "skills" slot would lose the pricing, which is the actual
// point of showing them here. (2) Two CTAs, not one "Connect" button —
// this component is embedded on human-facing marketing pages, not just
// an agent-to-agent connection moment, so it needs both a human path
// (sign up to the dashboard) and an agent path (copy the real MCP URL).
//
// FIXED (this pass) — uid was previously hardcoded to FDW's own entity
// (FDW_UID), with no prop to override it. RegistryView.tsx renders this
// component generically for ANY entity's /registry/:uid profile page,
// with no way to tell it which entity's card to fetch — every profile
// page silently showed FDW's own card instead of the entity actually
// being viewed. This went unnoticed because FDW_UID happens to be the
// entity used throughout this session's testing, making the bug
// invisible by coincidence rather than by correctness. uid is now an
// optional prop, defaulting to FDW_UID only when omitted — RegistryView
// must now pass its own route uid explicitly (see that file's own
// update, same pass).

import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Loader2Icon} from 'lucide-react';
import { ConnectActions } from './ConnectActions';

const FDW_UID = '3vuuC2kHE5ciUd1r3IKNVs7SEfp1';

interface OutcomePriceModel {
  kind: 'fixed' | 'hourly' | 'range';
  amountCents: number | null;
  minCents: number | null;
  maxCents: number | null;
}

interface CardOutcome {
  id: string;
  name: string;
  priceModel: OutcomePriceModel;
  priceUnit: string | null;
}

interface CardSkill {
  id: string;
  name: string;
  description: string;
}

interface AgentCardData {
  name: string;
  description: string;
  version: string;
  provider?: { organization: string };
  skills: CardSkill[];
  'fdw:outcomes': CardOutcome[];
}

function formatPrice(priceModel: OutcomePriceModel, priceUnit: string | null): string {
  const unit = priceUnit ? ` ${priceUnit}` : '';
  if (priceModel.kind === 'fixed' && priceModel.amountCents != null) {
    return `$${(priceModel.amountCents / 100).toLocaleString()}${unit}`;
  }
  if (priceModel.kind === 'range' && priceModel.minCents != null && priceModel.maxCents != null) {
    return `$${(priceModel.minCents / 100).toLocaleString()}–$${(priceModel.maxCents / 100).toLocaleString()}${unit}`;
  }
  if (priceModel.kind === 'hourly' && priceModel.amountCents != null) {
    return `$${(priceModel.amountCents / 100).toLocaleString()}/hr`;
  }
  return 'Contact for pricing';
}

interface AgentCardProps {
  uid?: string;
  onSignUpClick?: () => void;
}

export const AgentCard: React.FC<AgentCardProps> = ({ uid, onSignUpClick }) => {
  const [data, setData] = useState<AgentCardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const targetUid = uid ?? FDW_UID;
  const cardUrl = `https://fivedollarworkday.com/agents/${targetUid}/agent-card.json`;

  useEffect(() => {
    setIsLoading(true);
    setData(null);
    fetch(cardUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => setData(json))
      .catch((err) => console.error('[AgentCard] Failed to load card:', err))
      .finally(() => setIsLoading(false));
  }, [cardUrl]);

  if (isLoading) {
    return (
      <Card className="border border-brand-border rounded-2xl w-full max-w-sm bg-white">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2Icon className="size-5 animate-spin text-brand-gray" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const outcomes = data['fdw:outcomes'] ?? [];
  const skills = data.skills ?? [];

  return (
    <Card className="border border-brand-border rounded-2xl w-full max-w-sm bg-white shadow-sm">
      <CardContent className="p-5 flex flex-col gap-4">

        {/* ── Header — icon, name, version, provider ─────────────── */}
          <div className="flex items-start gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-brand-surface">
              <img src="/favicon.svg" alt="" className="size-6 object-contain" />
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
            <span className="flex items-baseline gap-2">
              <span className="truncate text-sm font-semibold text-brand-black">
                {data.name || 'Five Dollar Workday'}
              </span>
              {data.version && (
                <span className="shrink-0 font-mono text-[10px] text-brand-gray">v{data.version}</span>
              )}
            </span>
            <span className="truncate text-xs text-brand-gray">
              {data.provider?.organization || 'Five Dollar Workday'}
            </span>
          </div>
        </div>

        {/* ── Description ─────────────────────────────────────────── */}
        <p className="text-xs leading-relaxed text-brand-charcoal">{data.description}</p>

        {/* ── Skills — what kind of interaction is possible ──────── */}
        {skills.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {skills.map((skill) => (
              <div key={skill.id} className="flex items-baseline gap-2">
                <span className="shrink-0 rounded-md bg-brand-surface px-1.5 py-0.5 font-mono text-[10px] text-brand-charcoal">
                  {skill.name}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs text-brand-gray">
                  {skill.description}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ── Outcomes — specific, priced work ────────────────────── */}
        {outcomes.length > 0 && (
          <div className="flex flex-col gap-1.5 border-t border-brand-border pt-3">
            {outcomes.map((outcome) => (
              <div key={outcome.id} className="flex items-center justify-between text-xs">
                <span className="truncate text-brand-charcoal">{outcome.name}</span>
                <span className="shrink-0 font-mono text-[10px] text-brand-gray">
                  {formatPrice(outcome.priceModel, outcome.priceUnit)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ── Endpoint metadata — ambient, always visible ─────────── */}
        <div className="flex items-center gap-2 border-t border-brand-border pt-3">
          <span className="min-w-0 flex-1 truncate font-mono text-[10px] text-brand-gray/70">
            {`https://fivedollarworkday.com/mcp/e/${targetUid}`}
          </span>
        </div>

        {/* ── Two distinct actions — human path, agent path ───────── */}
        <ConnectActions onSignUpClick={onSignUpClick} />

      </CardContent>
    </Card>
  );
};